import { getIntersectionRoot, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Bewegung der Markenseiten: Einblenden beim Scrollen, Lichtkegel unter dem
 * Zeiger, Parallaxe auf Bildern und Ausblenden von Hero-Inhalten.
 *
 * Der Entwurf löst das Einblenden über einen einzigen IntersectionObserver mit
 * `threshold: .16`, der nach dem ersten Treffer nicht mehr hinschaut. Das ist
 * hier übernommen. Drei Dinge sind anders, alle drei erzwungen:
 *
 * ⚠️ **Der Beobachter braucht einen `root`.** Horizon lässt ab 990 px nicht das
 * Fenster scrollen, sondern `.page-wrapper`. Ein Beobachter ohne `root` misst
 * dort gegen den Viewport, in dem sich nichts bewegt — die Blöcke blieben am
 * Desktop dauerhaft auf `opacity: 0` stehen.
 *
 * ⚠️ **Das Scroll-Ereignis kommt aus derselben Ecke.** Der Entwurf hängt am
 * `window` und liest `window.scrollY`. Beides ist am Desktop tot: Das Ereignis
 * feuert an `.page-wrapper`, und `scrollY` bleibt 0. Die Parallaxe rechnet
 * deshalb aus `getBoundingClientRect()` — das ist unabhängig davon, welcher
 * Container scrollt — und hört an `getScrollEventTarget()` zu.
 *
 * Beide Bezüge wechseln beim Überqueren der 990-px-Grenze, deshalb wird bei
 * `scrollContainerMediaQuery` neu aufgebaut.
 *
 * ⚠️ **Neu binden bei `shopify:section:load`.** Der Theme-Editor rendert eine
 * Section nach jeder Änderung neu. Ohne erneutes Beobachten trägt das frische
 * Markup zwar den Startzustand, aber niemand nimmt ihn ihm wieder ab — die
 * Section steht dann leer da, ohne Fehlermeldung.
 *
 * Ohne JavaScript passiert nichts davon: Die Startzustände hängen an der Klasse
 * `cd-motion`, die erst gesetzt wird, wenn dieses Modul überhaupt laufen kann
 * (siehe `snippets/cd-motion.liquid`). Der Inhalt bleibt sichtbar.
 */

/** Wie viel eines Blocks sichtbar sein muss, bevor er einblendet. */
const THRESHOLD = 0.16;
const HOVER_QUERY = window.matchMedia('(hover: hover)');
const REDUCED_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');
const DESKTOP_QUERY = window.matchMedia('(min-width: 768px)');

const REVEAL_SELECTOR = '[data-cd-reveal]';
const SPOTLIGHT_SELECTOR = '[data-cd-spotlight]';
const PARALLAX_SELECTOR = '[data-cd-parallax]';
const FADE_SELECTOR = '[data-cd-fade]';
/** Anteil der Rahmenhöhe, über den ausgeblendet wird, wenn nichts anderes dasteht. */
const FADE_SPAN = 0.85;
const IN_CLASS = 'is-in';

class Motion {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {{ host: Element, image: HTMLImageElement, amount: number }[]} */
  #parallax = [];
  /** @type {{ host: Element, el: HTMLElement, shift: number, distance: number }[]} */
  #fades = [];
  /** @type {EventTarget | null} */
  #scrollTarget = null;
  #frame = 0;
  #spotlightFrame = 0;
  /** @type {{ card: HTMLElement, x: number, y: number } | null} */
  #spotlightPending = null;

  start() {
    this.#build();

    // Der Editor tauscht Markup aus; die neuen Blöcke kennt noch niemand.
    document.addEventListener('shopify:section:load', () => this.#build());

    // Über der Grenze wechseln Beobachter-Wurzel und Scroll-Ziel.
    scrollContainerMediaQuery.addEventListener('change', () => this.#build());
    DESKTOP_QUERY.addEventListener('change', () => this.#build());

    window.addEventListener('resize', this.#onScroll, { passive: true });

    // Der Bewegungswunsch kann sich im laufenden Betrieb ändern. Ohne das
    // liefen Parallaxe und Ausblendung weiter, obwohl der Nutzer sie eben
    // abgestellt hat — und die CSS-Sicherung greift nicht, weil Inline-Styles
    // aus `#update()` jede Autorenregel schlagen.
    REDUCED_QUERY.addEventListener('change', () => this.#build());

    // Der Lichtkegel hängt an einem einzigen Zuhörer; ihn immer zu binden ist
    // billiger als zu prüfen, ob später eine Maus dazukommt.
    this.#watchPointer();
  }

  #build() {
    this.#observeReveals();
    this.#bindScroll();
  }

  #observeReveals() {
    this.#observer?.disconnect();

    if (prefersReducedMotion()) {
      // Kein Beobachter nötig: alles sofort da, die Übergänge sind per
      // Media-Query ohnehin abgeschaltet.
      for (const el of document.querySelectorAll(REVEAL_SELECTOR)) el.classList.add(IN_CLASS);
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add(IN_CLASS);
          this.#observer?.unobserve(entry.target);
        }
      },
      { root: getIntersectionRoot(), threshold: THRESHOLD }
    );

    for (const el of document.querySelectorAll(REVEAL_SELECTOR)) {
      this.#stagger(el);
      if (el.classList.contains(IN_CLASS)) continue;
      this.#observer.observe(el);
    }
  }

  /**
   * Laufindex für die Kaskade. Der Entwurf nagelt sie über `:nth-child(1..6)`
   * fest — ab dem siebten Kind fehlt die Verzögerung dort wortlos. Als
   * Custom-Property wächst die Staffelung mit der Zahl der Blöcke.
   *
   * @param {Element} el
   */
  #stagger(el) {
    let index = 0;
    for (const child of el.children) {
      if (!(child instanceof HTMLElement)) continue;
      // Horizons Button-Block legt ein `<style>` neben den Knopf. Es rendert
      // nichts, würde aber einen Schritt der Kaskade verbrauchen und alles
      // Nachfolgende um 100 ms verschieben.
      if (child.tagName === 'STYLE' || child.tagName === 'SCRIPT') continue;
      child.style.setProperty('--cd-i', String(index));
      index += 1;
    }
  }

  #bindScroll() {
    if (this.#scrollTarget) this.#scrollTarget.removeEventListener('scroll', this.#onScroll);
    this.#scrollTarget = null;

    if (prefersReducedMotion()) {
      for (const { image } of this.#parallax) image.style.removeProperty('transform');
      for (const { el } of this.#fades) {
        el.style.removeProperty('transform');
        el.style.removeProperty('opacity');
      }
      this.#parallax = [];
      this.#fades = [];
      return;
    }

    // Das Attribut sitzt am Rahmen, das Bild liegt darin. Beides zusammen
    // sammeln, damit pro Bild nur einmal gesucht werden muss statt in jedem Frame.
    this.#parallax = [];
    for (const host of document.querySelectorAll(PARALLAX_SELECTOR)) {
      const image = host.querySelector('img');
      const amount = Number.parseFloat(/** @type {HTMLElement} */ (host).dataset.cdParallax ?? '0');
      // Der Entwurf fährt die Hero-Parallaxe erst ab 768 px. Auf dem Telefon
      // klebt die Figur an der Unterkante; jede Verschiebung reisst dort oben
      // einen Streifen blanker Fläche auf.
      const nurDesktop = 'cdParallaxDesktop' in /** @type {HTMLElement} */ (host).dataset;
      if (!image || !amount) continue;
      if (nurDesktop && !DESKTOP_QUERY.matches) {
        image.style.removeProperty('transform');
        continue;
      }
      this.#parallax.push({ host, image, amount });
    }

    // Ausblenden bezieht sich immer auf das Elternelement: Es ist der Rahmen,
    // dessen Sichtstrecke die Strecke des Effekts vorgibt.
    this.#fades = [];
    for (const el of document.querySelectorAll(FADE_SELECTOR)) {
      const host = el.parentElement;
      if (!(el instanceof HTMLElement) || !host) continue;
      const shift = Number.parseFloat(el.dataset.cdFade ?? '0') || 0;
      const distance = Number.parseFloat(el.dataset.cdFadeDistance ?? '') || 0;
      this.#fades.push({ host, el, shift, distance });
    }

    if (this.#parallax.length === 0 && this.#fades.length === 0) return;

    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#update();
  }

  #onScroll = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(this.#update);
  };

  /**
   * Der Ausschlag ist auf die Sichtstrecke des **Elternelements** normiert:
   * `-1` wenn es gerade unten hereinkommt, `+1` wenn es oben verschwindet.
   * Deshalb steht das Bild in der Bildmitte genau auf Null und wandert nach
   * beiden Seiten gleich weit — der Entwurf rechnet identisch.
   */
  #update = () => {
    this.#frame = 0;
    const viewport = window.innerHeight;

    for (const { host, image, amount } of this.#parallax) {
      const rect = host.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > viewport) continue;

      const progress = ((rect.top + rect.height / 2 - viewport / 2) / (viewport + rect.height)) * 2;
      image.style.transform = `translateY(${(-progress * amount).toFixed(1)}px)`;
    }

    for (const { host, el, shift, distance } of this.#fades) {
      const rect = host.getBoundingClientRect();
      if (rect.bottom < 0) continue;

      // Wie weit der Rahmen oben schon herausgeschoben ist. Ohne eigene
      // Strecke sind es 85 % seiner Höhe — der Wert des Entwurfs.
      const travelled = Math.max(0, -rect.top);
      const span = distance || rect.height * FADE_SPAN;
      const progress = span > 0 ? Math.min(1, travelled / span) : 0;

      el.style.opacity = (1 - progress).toFixed(3);
      if (shift) el.style.transform = `translateY(${(travelled * shift).toFixed(1)}px)`;
    }
  };

  /**
   * Der Lichtkegel hängt an einem einzigen Zuhörer am Dokument statt an jeder
   * Karte. So überlebt er jedes Neurendern im Editor, ohne neu gebunden zu
   * werden — und es bleiben keine Zuhörer auf Karten zurück, die längst weg sind.
   */
  #watchPointer() {
    document.addEventListener(
      'pointermove',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (!HOVER_QUERY.matches) return;
        const card = target.closest(SPOTLIGHT_SELECTOR);
        if (!(card instanceof HTMLElement)) return;

        this.#spotlightPending = { card, x: event.clientX, y: event.clientY };
        if (this.#spotlightFrame) return;

        this.#spotlightFrame = requestAnimationFrame(() => {
          this.#spotlightFrame = 0;
          const pending = this.#spotlightPending;
          this.#spotlightPending = null;
          if (!pending) return;
          const rect = pending.card.getBoundingClientRect();
          pending.card.style.setProperty('--mx', `${pending.x - rect.left}px`);
          pending.card.style.setProperty('--my', `${pending.y - rect.top}px`);
        });
      },
      { passive: true }
    );
  }
}

// Dem Sicherheitsnetz in `snippets/cd-motion.liquid` melden, dass das Modul
// wirklich läuft. Ohne dieses Lebenszeichen nimmt es die Klasse `cd-motion`
// nach kurzer Zeit wieder ab — sonst blieben die Startzustände (`opacity: 0`)
// für immer stehen, wenn das Modul scheitert.
window.__cdMotionReady?.();

new Motion().start();
